/**
 * AppLayout: AppBar + persistent Drawer + Outlet. Rendered inside AuthGate, so
 * it only mounts when authenticated.
 *
 * Product-admin gate: an authenticated user without the `productAdminOf` JWT
 * claim sees ProductAdminRequired in-place — no redirect loop. The gate is
 * in-component by design; it is never a route loader.
 */
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { useCurrentProductAdmin, useAuth, type CurrentProductAdmin } from '@/auth/AuthContext';
import { authApi } from '@/api/auth';
import { clearDeepLink } from '@/auth/silentRefresh';
import { env } from '@/config/env';
import { NAV_GROUPS, SETTINGS_NAV_ITEM, type NavItem } from './nav';
import { readNavCollapsed, writeNavCollapsed } from './navPreference';
import { NavBadgeProvider, useNavBadges } from '@/hooks/useNavBadges';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LoadingSpinner } from './LoadingSpinner';
import { PreAppLayout } from './PreAppLayout';
import { ProductAdminRequired } from './ProductAdminRequired';
import { NotifyProvider } from './page';
import { PageBannerRegion } from './page/PageBannerRegion';
import { COLOR_SCHEME_MODES, type ColorSchemeMode } from '@/theme/colorScheme';
import { CommandPalette, PaletteTrigger } from './palette/CommandPalette';
import { useScrollRestoration } from './useScrollRestoration';

const DRAWER_WIDTH = 246;
/** Collapsed icon rail on md+ (SHELL-004). Below md the temporary drawer is always expanded. */
const RAIL_WIDTH = 64;
const CONTENT_MAX_WIDTH = 1480;

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** Two-letter initials from name, else the first letter of the email. */
function getInitials(productAdmin: CurrentProductAdmin): string {
  if (productAdmin.name) {
    const parts = productAdmin.name.trim().split(/\s+/);
    const first = parts[0];
    const last = parts[parts.length - 1];
    if (parts.length >= 2 && first && last) {
      return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }
  return (productAdmin.email[0] ?? '?').toUpperCase();
}

/**
 * An open, visible modal dialog outside the nav drawer. A kept-mounted modal that is closed is
 * hidden: its own modal root is `.MuiModal-hidden`, or it is `visibility: hidden`, so it never
 * counts. TMMPDM-0046: "open" is decided from the dialog's own modal root only, never from an
 * `aria-hidden` ancestor — MUI marks an open dialog's root `aria-hidden` while a newer modal (a
 * Select's menu inside it) is open, and that dialog is still open.
 */
function openTaskDialog(navDrawer: Element | null): boolean {
  return Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(
    (dialog) =>
      !navDrawer?.contains(dialog) &&
      !dialog.closest('.MuiModal-root')?.classList.contains('MuiModal-hidden') &&
      window.getComputedStyle(dialog).visibility !== 'hidden',
  );
}

function capitalize(value: string): string {
  return value.length > 0 ? value[0]!.toUpperCase() + value.slice(1) : value;
}

export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readNavCollapsed);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { t } = useCopy();
  const productAdmin = useCurrentProductAdmin();
  const { status, signOut: ctxSignOut } = useAuth();
  // TMMPDM-0039: Light / Dark / System. MUI applies and persists the choice; System is the default.
  const { mode, setMode } = useColorScheme();
  const currentScheme: ColorSchemeMode = mode ?? 'system';
  const navigate = useNavigate();
  const location = useLocation();
  // TMMPDM-0040: the command palette — the top-bar control or Ctrl/Cmd+K, never over another dialog.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const themeLabelId = useId();
  const mainRef = useRef<HTMLElement>(null);

  // Route focus (TMM-B7, TMM profile 1.1): on a path change — never on a `?tab=` or filter change —
  // focus moves to the page's `h1`, but only after the person's first interaction (a key, a pointer
  // press or a click) in this load. Before it no navigation moves focus, whatever its kind: a cold
  // load, a redirect off it or a deep link settling must leave the skip link the first Tab stop.
  const pathname = location.pathname;
  const navigationType = useNavigationType();
  const lastPath = useRef<string | null>(null);
  const firstKey = useRef(location.key);
  const interacted = useRef(false);
  const pageDrawn = useRef(false);
  // The top of the page is a step of its own, due once per PUSH or REPLACE the person starts (not
  // the initial route or a redirect off it), whatever route focus then does: an early click or key
  // cancels the focus, never the scroll. A Back or Forward (POP) does not go to the top: it returns
  // to where the person was, once the page has settled (useScrollRestoration, NAV-010).
  const scrollDue = useRef(false);
  useScrollRestoration(mainRef);
  const originRef = useRef<Element | null>(null);
  useLayoutEffect(() => {
    originRef.current = document.activeElement;
  }, [pathname]);
  // A late page title (a loading skeleton's h1 that resolves) is announced here, once, politely.
  const liveRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const mark = () => {
      interacted.current = true;
    };
    const events = ['keydown', 'pointerdown', 'click'] as const;
    for (const type of events) document.addEventListener(type, mark, true);
    return () => {
      for (const type of events) document.removeEventListener(type, mark, true);
    };
  }, []);
  useEffect(() => {
    // A REPLACE off the initial route is a redirect until a page has rendered its `h1`: a cold load
    // through `/` or a legacy URL is still a load (TMMPDM-0070 A3). After that — a Save, a Copy, a
    // Delete — it is a navigation the person started.
    const started =
      location.key !== firstKey.current && (navigationType !== 'REPLACE' || pageDrawn.current);
    scrollDue.current = started && navigationType !== 'POP';
    // After the decision: this commit's page may be the first to draw a heading.
    if (!pageDrawn.current && mainRef.current?.querySelector('h1')) pageDrawn.current = true;
  }, [location.key, navigationType]);
  useEffect(() => {
    const previous = lastPath.current;
    lastPath.current = pathname;
    if (previous === null || previous === pathname) return;
    if (scrollDue.current) {
      scrollDue.current = false;
      window.scrollTo(0, 0);
    }
    const live = liveRef.current;
    if (live) live.textContent = '';
    if (!interacted.current) return;
    const main = mainRef.current;
    if (!main) return;
    // Where the navigation left focus: the link or control that was used, or the body when that
    // control went away with the page. Read at commit (below), before the new page's own effects
    // can place focus: what the page focuses is not the control the person used.
    const origin = originRef.current;
    let cancelled = false;
    let focused: HTMLElement | null = null;
    let pending: number | undefined;
    let announceTimer: number | undefined;
    let titleObserver: MutationObserver | undefined;
    let landedText = '';
    const headingText = (heading: Element | null) => (heading?.textContent ?? '').trim();
    // Once route focus has landed, the first settled change of the title is announced — focus is
    // never moved again for it. The debounce is trailing, so an intermediate title ("Loading…" →
    // "Partner" → "Acme") is never announced; and a heading that took focus again is not repeated.
    const announceTitle = () => {
      announceTimer = undefined;
      const text = headingText(main.querySelector('h1'));
      if (text === '' || text === landedText) return;
      landedText = text;
      titleObserver?.disconnect();
      if (live) live.textContent = text;
    };
    const watchTitle = () => {
      if (titleObserver) return;
      titleObserver = new MutationObserver(() => {
        window.clearTimeout(announceTimer);
        announceTimer = window.setTimeout(announceTitle, 250);
      });
      titleObserver.observe(main, { childList: true, subtree: true, characterData: true });
    };
    // Judged a moment after the page settles, never inside the mutation itself: a blur handler can
    // change the DOM while focus is between two controls, and that moment is not "idle".
    const settle = () => {
      pending = undefined;
      if (cancelled) return;
      const active = document.activeElement;
      // The PAGE already placed focus inside <main> (an error state, a wizard step): leave it. The
      // control the person used is not the page's doing, even when it survives the navigation.
      if (active && active !== main && active !== origin && main.contains(active)) return;
      if (active !== document.body && active !== origin) return;
      if (focused?.isConnected) return;
      const heading = main.querySelector<HTMLElement>('h1');
      if (!heading) return;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      // Focus without scrolling; the top of the page was scrolled to when the navigation committed.
      heading.focus({ preventScroll: true });
      focused = heading;
      landedText = headingText(heading);
      watchTitle();
    };
    const schedule = () => {
      if (pending === undefined) pending = window.setTimeout(settle, 50);
    };
    // Any press after the navigation is the person acting — a click inside <main> (which takes
    // focus itself) or on a button that removes itself is not "where navigation left it".
    const cancel = () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(pending);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(main, { childList: true, subtree: true });
    window.addEventListener('pointerdown', cancel, true);
    window.addEventListener('keydown', cancel, true);
    schedule();
    return () => {
      cancelled = true;
      observer.disconnect();
      titleObserver?.disconnect();
      window.clearTimeout(pending);
      window.clearTimeout(announceTimer);
      window.removeEventListener('pointerdown', cancel, true);
      window.removeEventListener('keydown', cancel, true);
    };
  }, [pathname]);

  // The nav drawer: its temporary variant is a keepMounted modal, so its paper is always a dialog.
  const navDrawerRef = useRef<HTMLDivElement>(null);
  // Focus was in the drawer when the palette displaced it: on close it returns to the Menu button,
  // never to a link in the now-hidden drawer (A11Y-010).
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const displacedDrawer = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 'k') {
        return;
      }
      // The browser never takes the keystroke, even when a task dialog keeps the palette shut.
      event.preventDefault();
      if (openTaskDialog(navDrawerRef.current)) return;
      // The open mobile drawer is navigation, not a task: it gives way to the palette.
      displacedDrawer.current = navDrawerRef.current?.contains(document.activeElement) ?? false;
      setMobileOpen(false);
      setPaletteOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSignOut = useCallback(async () => {
    setMenuAnchor(null);
    try {
      await authApi.logout();
    } catch {
      /* logout errors are not blocking — proceed to the signed-out page */
    }
    clearDeepLink();
    ctxSignOut();
    navigate('/auth/signed-out', { replace: true });
  }, [ctxSignOut, navigate]);

  // Product-admin gate: render before drawing the shell.
  if (status === 'booting') {
    return (
      <PreAppLayout>
        <LoadingSpinner />
      </PreAppLayout>
    );
  }
  if (status === 'authed' && !productAdmin) return <ProductAdminRequired />;

  const initials = productAdmin ? getInitials(productAdmin) : '?';
  // The icon rail exists on md+ only; the temporary drawer below md is always expanded.
  const rail = isDesktop && collapsed;
  const drawerWidth = rail ? RAIL_WIDTH : DRAWER_WIDTH;
  const toggleLabel = collapsed
    ? t('shell.expandNav', 'Expand navigation')
    : t('shell.collapseNav', 'Collapse navigation');

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeNavCollapsed(next);
  };

  const drawerContent = (
    <Box sx={{ width: drawerWidth, display: 'flex', flexDirection: 'column', flex: 1 }}>
      {NAV_GROUPS.map((group, idx) => (
        <Box key={group.groupKey} sx={{ mt: idx === 0 ? 0.5 : 1 }}>
          {/* Section heading lives outside <List> so the <ul> only contains <li> children.
              The rail shows icons only — every label is in its item's tooltip and name. */}
          {rail || !group.groupFallback ? null : (
            <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
              <Typography
                variant="caption"
                component="p"
                sx={{ letterSpacing: '0.08em', fontWeight: 600, color: 'text.secondary' }}
              >
                {t(group.groupKey, group.groupFallback)}
              </Typography>
            </Box>
          )}
          <List dense>
            {group.items.map((item) => (
              <NavLinkItem
                key={item.to}
                item={item}
                rail={rail}
                onClick={() => setMobileOpen(false)}
                t={t}
              />
            ))}
          </List>
          {idx < NAV_GROUPS.length - 1 ? <Divider sx={{ my: 1 }} /> : null}
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {/* Settings is not business navigation: pinned at the bottom, separated (NAV-005). */}
      <Divider />
      <List dense>
        <NavLinkItem
          item={SETTINGS_NAV_ITEM}
          rail={rail}
          onClick={() => setMobileOpen(false)}
          t={t}
        />
      </List>
      {isDesktop ? (
        <Box sx={{ display: 'flex', justifyContent: rail ? 'center' : 'flex-end', px: 1, pb: 1 }}>
          <Tooltip title={toggleLabel} placement="right">
            <IconButton onClick={toggleCollapsed} aria-label={toggleLabel} aria-expanded={!rail}>
              {rail ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <NavBadgeProvider>
      <NotifyProvider inset={isDesktop ? drawerWidth : 0}>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          {/* The first focusable element (TMM-B7): shown on focus, it moves focus into <main>. */}
          <Link
            href="#main"
            onClick={(event) => {
              event.preventDefault();
              mainRef.current?.focus();
            }}
            sx={{
              position: 'fixed',
              top: 8,
              left: 8,
              zIndex: (th) => th.zIndex.tooltip + 1,
              px: 2,
              py: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              color: 'text.primary',
              transform: 'translateY(-200%)',
              '&:focus': { transform: 'none' },
            }}
          >
            {t('shell.skipToMain', 'Skip to main content')}
          </Link>
          <AppBar
            position="fixed"
            color="default"
            elevation={0}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              zIndex: (th) => th.zIndex.drawer + 1,
            }}
          >
            <Toolbar>
              {!isDesktop ? (
                <Tooltip title={t('common.menu', 'Menu')}>
                  <IconButton
                    ref={menuButtonRef}
                    edge="start"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label={t('common.menu', 'Menu')}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Typography
                variant="h3"
                component={NavLink}
                to="/"
                sx={{
                  ml: isDesktop ? 0 : 1,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                {t('app.shellTitle', 'TMM Product Admin · Platform Console')}
              </Typography>
              {/* Environment banner. */}
              <Chip label={capitalize(env.deployEnv)} size="small" sx={{ ml: 2 }} />
              {/* Scope label — this console is platform-scoped, not per-tenant. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 2,
                  color: 'text.secondary',
                }}
              >
                <BusinessIcon fontSize="small" aria-hidden="true" />
                <Typography variant="body2">
                  {t('shell.scope', 'Platform — all tenants')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <PaletteTrigger compact={!isDesktop} onOpen={() => setPaletteOpen(true)} />
              <LanguageSwitcher />
              <Tooltip title={productAdmin?.email ?? t('shell.accountMenu', 'Account')}>
                <IconButton
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label={t('shell.accountMenu', 'Account')}
                  sx={{ p: 0.5 }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: 'primary.main',
                      typography: 'subtitle2',
                    }}
                  >
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {productAdmin ? (
                  <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
                    {productAdmin.name ? (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {productAdmin.name}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="body2"
                      sx={{ color: productAdmin.name ? 'text.secondary' : 'text.primary' }}
                    >
                      {productAdmin.email}
                    </Typography>
                  </Box>
                ) : null}
                <Divider />
                {/* A preference, not business navigation: it lives in the user menu (NAV-005). The
                    three options are one radio set; a check mark, not colour, marks the current.
                    TMMPDM-0044 (A11Y-006): the set is a group named by its "Theme" caption. */}
                <Typography
                  id={themeLabelId}
                  variant="caption"
                  component="div"
                  sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600, color: 'text.secondary' }}
                >
                  {t('shell.theme.label', 'Theme')}
                </Typography>
                <Box role="group" aria-labelledby={themeLabelId}>
                  {COLOR_SCHEME_MODES.map((scheme) => {
                    const checked = scheme === currentScheme;
                    return (
                      <MenuItem
                        key={scheme}
                        role="menuitemradio"
                        aria-checked={checked}
                        onClick={() => {
                          setMode(scheme);
                          setMenuAnchor(null);
                        }}
                      >
                        <ListItemIcon>
                          {checked ? <CheckIcon fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText>
                          {t(`shell.theme.${scheme}`, capitalize(scheme))}
                        </ListItemText>
                      </MenuItem>
                    );
                  })}
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => {
                    void handleSignOut();
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('nav.signOut', 'Sign out')}</ListItemText>
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          <Drawer
            ref={navDrawerRef}
            variant={isDesktop ? 'permanent' : 'temporary'}
            open={isDesktop ? true : mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              width: drawerWidth,
              transition: (th) => th.transitions.create('width'),
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: 'border-box',
                overflowX: 'hidden',
                transition: (th) => th.transitions.create('width'),
              },
            }}
          >
            <Toolbar />
            {drawerContent}
          </Drawer>
          <Box
            component="main"
            id="main"
            ref={mainRef}
            tabIndex={-1}
            sx={{
              '&:focus': { outline: 'none' },
              flexGrow: 1,
              width: { md: `calc(100% - ${drawerWidth}px)` },
              ml: { md: 0 },
              minHeight: '100vh',
              bgcolor: 'background.default',
            }}
          >
            <Toolbar />
            <Container
              maxWidth={false}
              sx={{ py: 4, maxWidth: CONTENT_MAX_WIDTH }}
              key={location.pathname.split('/')[1] ?? 'root'}
            >
              <PageBannerRegion>
                <Outlet />
              </PageBannerRegion>
            </Container>
          </Box>
          {/* A late page title, announced once (TMM-B7); in the layout so it outlives the page. */}
          <Box component="span" ref={liveRef} aria-live="polite" sx={visuallyHidden} />
        </Box>
        <CommandPalette
          open={paletteOpen}
          onClose={() => {
            setPaletteOpen(false);
            if (!displacedDrawer.current) return;
            displacedDrawer.current = false;
            // After the dialog's own focus restore, which targets the hidden drawer.
            window.setTimeout(() => menuButtonRef.current?.focus());
          }}
        />
      </NotifyProvider>
    </NavBadgeProvider>
  );
}

interface NavLinkItemProps {
  item: NavItem;
  /** Icon rail: the label moves into the tooltip and the accessible name (SHELL-004). */
  rail: boolean;
  onClick: () => void;
  t: (key: string, fallback?: string) => string;
}

function NavLinkItem({ item, rail, onClick, t }: NavLinkItemProps) {
  const badges = useNavBadges();
  const count = item.badge ? badges[item.badge] : undefined;
  const label = t(item.key, item.fallback);
  // ListItem renders as <li>, satisfying the <ul> → <li> HTML requirement.
  // ListItemButton renders as <a> when component={NavLink} — it must live
  // inside the <li>, not directly in the <ul>. NavLink sets aria-current="page" when active.
  // Selected = a 3px primary bar + subtitle2 weight + the tint — never colour alone (NAV-001).
  const button = (
    <ListItemButton
      component={NavLink}
      to={item.to}
      onClick={onClick}
      {...(rail ? { 'aria-label': label } : {})}
      sx={{
        ...(rail ? { justifyContent: 'center', px: 1 } : {}),
        '&.active': {
          bgcolor: 'action.selected',
          boxShadow: (th) => `inset 3px 0 0 ${(th.vars ?? th).palette.primary.main}`,
        },
        '&.active .MuiListItemText-primary': {
          fontWeight: (th) => th.typography.subtitle2.fontWeight,
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: rail ? 0 : 36 }}>
        {rail && count !== undefined ? (
          <Badge badgeContent={count} color="primary" max={999}>
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>
      {rail ? null : <ListItemText primary={label} />}
      {!rail && count !== undefined ? (
        <Chip label={count} size="small" sx={{ height: 20 }} />
      ) : null}
    </ListItemButton>
  );
  return (
    <ListItem disablePadding>
      {rail ? (
        <Tooltip title={label} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
}
